/**
 * Casual Leave identity helpers for the UI.
 *
 * Authoritative backend identity is tenant_property.casualLeaveId.
 * Quarterly accounting is authoritative only when casualLeaveAccountingEnabled === true
 * (employee currently has a Leave Group). Leave name/ID identify Casual Leave but must
 * NOT by themselves skip annual quota behavior.
 */

export const isCasualLeaveName = (leaveName) =>
  leaveName && String(leaveName).toLowerCase().includes('casual');

/**
 * True when quarterly Casual Leave accounting is authoritative for this leave record.
 */
export const isQuarterlyCasualAccounting = (leave) =>
  leave?.casualLeaveAccountingEnabled === true;

/**
 * Maps backend LeaveBand enum to UI Band 1/2/3 labels.
 * HIGHEST -> Band 1, MIDDLE -> Band 2, LOWEST -> Band 3.
 * Returns null for unknown/missing values (caller should omit the chip).
 */
export const formatCasualLeaveBandLabel = (band) => {
  if (band == null || band === '') {
    return null;
  }
  const normalized = String(band).toUpperCase();
  if (normalized === 'HIGHEST') {
    return 'Band 1';
  }
  if (normalized === 'MIDDLE') {
    return 'Band 2';
  }
  if (normalized === 'LOWEST') {
    return 'Band 3';
  }
  return null;
};

/**
 * Identifies Casual Leave by configured id or name.
 * Does not imply quarterly accounting — use isQuarterlyCasualAccounting for that.
 *
 * @param {object} leave leave record or leave-type row
 * @param {string} [leaveTypeName] optional display name when not on leave
 * @param {number|string|null} [casualLeaveId] configured tenant casualLeaveId when known
 */
export const isCasualLeaveRecord = (leave, leaveTypeName, casualLeaveId) => {
  const leaveId = leave?.leaveId ?? leave?.id;
  if (casualLeaveId != null && leaveId != null) {
    return String(leaveId) === String(casualLeaveId);
  }
  const name = leaveTypeName ?? leave?.leaveName ?? leave?.label ?? leave?.name;
  return isCasualLeaveName(name);
};
